import { useCallback, useState } from "react";
import { isNil } from "lodash";
import { AiccAssetUploadPreview, AiccAssetVersions } from "types/model/aiccLearningAssets";
import { RunWrite } from "./useLearningAssetVersions";
import { confirmUploadLearningAsset, skipLearningAssetConfirm } from "../confirmations";

interface ImportableApi<T, Preview, Row> {
  validateUpload: (languageId: number, file: File) => Promise<Preview>;
  replaceAll: (languageId: number, rows: Row[]) => Promise<AiccAssetVersions<T>>;
}

interface Options<T, Preview, Row> {
  api: ImportableApi<T, Preview, Row>;
  languageId?: number;
  isDraft: boolean;
  runWrite: RunWrite<T>;
}

export function useAssetImport<T, Row, Preview extends AiccAssetUploadPreview<Row>>({
  api,
  languageId,
  isDraft,
  runWrite,
}: Options<T, Preview, Row>) {
  const [uploadFile, setUploadFile] = useState<File>();
  const [preview, setPreview] = useState<Preview>();
  const [isValidating, setIsValidating] = useState(false);

  const closeUpload = useCallback(() => {
    setUploadFile(undefined);
    setPreview(undefined);
  }, []);

  const selectFile = useCallback(
    (file: File) => {
      if (!isNil(languageId)) {
        setIsValidating(true);
        api
          .validateUpload(languageId, file)
          .then((result) => {
            setUploadFile(file);
            setPreview(result);
          })
          .catch(() => undefined)
          .finally(() => setIsValidating(false));
      }
      return false;
    },
    [api, languageId]
  );

  const confirmUpload = useCallback(() => {
    if (isNil(preview)) {
      return;
    }
    runWrite((id) => api.replaceAll(id, preview.rows), "Uploaded successfully.", closeUpload, {
      confirm: isDraft ? skipLearningAssetConfirm : confirmUploadLearningAsset,
    });
  }, [api, closeUpload, isDraft, preview, runWrite]);

  return { uploadFile, preview, isValidating, closeUpload, selectFile, confirmUpload };
}
